import React, { useState } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  blurDataURL?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  priority = false,
  blurDataURL,
  objectFit = 'cover',
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generate WebP and fallback URLs
  const getImageUrls = (originalSrc: string) => {
    // If it's already a WebP or has query params, use as-is
    if (originalSrc.includes('.webp') || originalSrc.includes('?')) {
      return { webp: originalSrc, fallback: originalSrc };
    }

    // Try to convert to WebP
    const webpSrc = originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    return { webp: webpSrc, fallback: originalSrc };
  };

  const { webp, fallback } = getImageUrls(src);

  // Generate srcSet for responsive images
  const generateSrcSet = (baseSrc: string) => {
    if (!width) return undefined;

    const sizes = [400, 800, 1200, 1600];
    return sizes
      .map((size) => {
        if (baseSrc.includes('?')) {
          return `${baseSrc}&w=${size} ${size}w`;
        }
        return `${baseSrc}?w=${size} ${size}w`;
      })
      .join(', ');
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const style: React.CSSProperties = {
    objectFit,
    width: width ? `${width}px` : '100%',
    height: height ? `${height}px` : '100%',
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Blur placeholder */}
      {!isLoaded && blurDataURL && (
        <div
          className="absolute inset-0 blur-xl scale-110 transition-opacity duration-300"
          style={{
            backgroundImage: `url(${blurDataURL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* Loading skeleton */}
      {!isLoaded && !blurDataURL && (
        <div className="absolute inset-0 bg-neutral-800 animate-pulse" />
      )}

      {/* Actual image */}
      <picture>
        {/* WebP source */}
        <source
          type="image/webp"
          srcSet={generateSrcSet(webp)}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />

        {/* Fallback source */}
        <source
          srcSet={generateSrcSet(fallback)}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />

        {/* Fallback img */}
        <img
          src={hasError ? '/placeholder-image.jpg' : fallback}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          style={style}
          className={`transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </picture>
    </div>
  );
};
